import React, { useState, useEffect } from 'react';
import { fetchOrdersByTagId } from '../../services/shipStationAPI';
import type { ShipstationOrder } from '../../types/apiTypes';
import { tagDictionaryMaker, getTagNameFromEmail } from '../../contexts/TagStorage';
import { fetchTags } from '../../services/shipStationAPI';
import { useAuth } from '../../contexts/AuthContext';
import { useOrdersListHideIssues } from '../../hooks/useOrdersListHideIssues';
import OrderBox from './OrderBox';
import OrderSearchModal from './OrderSearchModal';
import OrdersListNavbarActions from './OrdersListNavbarActions';
import { filterOrdersHidingIssues } from './orderSearchUtils';
import { buildOrderDetailNavState } from './orderdetail/orderDetailNavState';
import './OrdersList.css';

function MyOrdersList(): React.ReactElement {
  const [orders, setOrders] = useState<ShipstationOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [tagDictionary, setTagDictionary] = useState<Record<string, string | number>>({});
  const [showOrderSearch, setShowOrderSearch] = useState(false);
  const { hideIssues, toggleHideIssues } = useOrdersListHideIssues();
  const { user } = useAuth();

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        const tagsData = await fetchTags({ forceRefresh: true });
        const tags = tagsData;
        const newTagDictionary = tagDictionaryMaker(tags);
        setTagDictionary(newTagDictionary);

        const userName = getTagNameFromEmail(user?.email ?? '', newTagDictionary) ?? '';
        const userShipStationTag = newTagDictionary[userName];

        if (userShipStationTag) {
          const data = await fetchOrdersByTagId(userShipStationTag);
          setOrders(data.orders || []);
        } else {
          setOrders([]);
        }
      } catch (error: unknown) {
        console.error('Failed to initialize My Orders:', error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      initialize();
    }
  }, [user?.email]);

  const navbarActions = (
    <OrdersListNavbarActions
      hideIssues={hideIssues}
      onToggleHideIssues={toggleHideIssues}
      onOpenSearch={() => setShowOrderSearch(true)}
    />
  );

  if (loading || !tagDictionary) {
    return (
      <div className="orders-container">
        {navbarActions}
        <p className="orders-list-status">Loading...</p>
      </div>
    );
  }

  const visibleOrders = filterOrdersHidingIssues(orders, tagDictionary, hideIssues);
  const navState = buildOrderDetailNavState('/my-orders', visibleOrders);

  return (
    <div className="orders-container">
      {navbarActions}
      {orders.length === 0 ? <p className="orders-list-status">No Orders</p> : null}
      <ul>
        {visibleOrders.map((order) => (
            <OrderBox
              key={order.orderId}
              order={order}
              tagDictionary={tagDictionary}
              navState={navState}
            />
        ))}
      </ul>
      <OrderSearchModal
        open={showOrderSearch}
        orders={visibleOrders}
        tagDictionary={tagDictionary}
        navState={navState}
        onClose={() => setShowOrderSearch(false)}
      />
    </div>
  );
}

export default MyOrdersList;
